<template>
  <div>
    <label>
      {{ $t(`${i18nPrefix}.LABEL`) }}
    </label>
    <multiselect
      v-model="selectedCompany"
      track-by="id"
      label="name"
      :placeholder="$t(`${i18nPrefix}.PLACEHOLDER`)"
      selected-label
      :select-label="$t('FORMS.MULTISELECT.CLICK_TO_SELECT')"
      :deselect-label="$t('FORMS.MULTISELECT.CLICK_TO_DESELECT')"
      :max-height="160"
      :options="companies"
      :allow-empty="true"
    />

    <div v-if="allowCreate" class="company-picker-actions">
      <woot-button
        variant="smooth"
        size="tiny"
        icon="add"
        class-names="company-picker-action"
        @click.prevent="openCreateModal"
      >
        {{ $t(`${i18nPrefix}.CREATE_NEW`) }}
      </woot-button>

      <woot-button
        v-if="selectedCompany"
        variant="smooth"
        size="tiny"
        icon="edit"
        class-names="company-picker-action"
        @click.prevent="openEditModal"
      >
        Editar
      </woot-button>

      <woot-button
        v-if="selectedCompany"
        variant="smooth"
        size="tiny"
        icon="delete"
        color-scheme="alert"
        class-names="company-picker-action"
        @click.prevent="openDeleteModal"
      >
        Deletar
      </woot-button>
    </div>

    <woot-modal
      v-if="allowCreate"
      :show.sync="showCreateModal"
      :on-close="handleCloseWithGuard"
      size="modal-fit"
    >
      <div class="column content-box">
        <woot-modal-header
          :header-title="
            isEditMode ? 'Editar empresa' : $t(`${i18nPrefix}.MODAL.TITLE`)
          "
          :header-content="
            isEditMode
              ? 'Atualize os dados da empresa'
              : $t(`${i18nPrefix}.MODAL.DESC`)
          "
        />
        <form class="row" @submit.prevent="submitCompany">
          <div class="medium-12 columns">
            <label :class="{ error: $v.newCompanyName.$error }">
              {{ $t(`${i18nPrefix}.MODAL.NAME.LABEL`) }}
              <input
                v-model.trim="newCompanyName"
                type="text"
                :placeholder="$t(`${i18nPrefix}.MODAL.NAME.PLACEHOLDER`)"
                @input="$v.newCompanyName.$touch()"
              />
            </label>
          </div>

          <div class="medium-12 columns">
            <label :class="{ error: $v.newCompanyDocument.$error }">
              {{ $t(`${i18nPrefix}.MODAL.DOCUMENT.LABEL`) }}
              <input
                v-model="newCompanyDocumentDisplay"
                type="text"
                :placeholder="$t(`${i18nPrefix}.MODAL.DOCUMENT.PLACEHOLDER`)"
                @input="$v.newCompanyDocument.$touch()"
              />
            </label>
          </div>

          <div class="modal-footer">
            <div class="medium-12 columns">
              <woot-submit-button
                :disabled="
                  $v.newCompanyName.$invalid ||
                    $v.newCompanyDocument.$invalid ||
                    isSaving
                "
                :button-text="
                  isEditMode ? 'Salvar' : $t(`${i18nPrefix}.MODAL.SUBMIT`)
                "
                :loading="isSaving"
                class="bg-primary"
              />
              <button class="button clear" @click.prevent="handleCloseWithGuard">
                {{ $t(`${i18nPrefix}.MODAL.CANCEL`) }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </woot-modal>

    <modal
      v-if="showDeleteCompanyModal"
      :show.sync="showDeleteCompanyModal"
      :on-close="closeDeleteModal"
      size="modal-fit"
      custom-class="company-picker-delete-modal"
    >
      <div class="company-picker-delete-content">
        <woot-modal-header
          :header-title="$t(`${i18nPrefix}.DELETE.TITLE`)"
          :header-content="
            $t(`${i18nPrefix}.DELETE.MESSAGE`, {
              companyName:
                selectedCompany && selectedCompany.name ? selectedCompany.name : '',
            })
          "
        />
        <div class="modal-footer delete-item">
          <woot-button
            type="button"
            variant="clear"
            class="action-button btn-primary"
            @click="closeDeleteModal"
          >
            {{ $t(`${i18nPrefix}.DELETE.REJECT`) }}
          </woot-button>
          <woot-button
            type="button"
            color-scheme="alert"
            class="action-button"
            :is-loading="isSaving"
            variant="smooth"
            @click="confirmDeleteCompany"
          >
            {{ $t(`${i18nPrefix}.DELETE.CONFIRM`) }}
          </woot-button>
        </div>
      </div>
    </modal>

    <!-- Modal de confirmação para dados não salvos -->
    <woot-delete-modal
      :show.sync="showUnsavedChangesConfirm"
      :on-close="cancelUnsavedClose"
      :on-confirm="confirmUnsavedClose"
      :title="$t('CONFIRM_UNSAVED_CHANGES.TITLE')"
      :message="$t('CONFIRM_UNSAVED_CHANGES.MESSAGE')"
      :confirm-text="$t('CONFIRM_UNSAVED_CHANGES.CONFIRM')"
      :reject-text="$t('CONFIRM_UNSAVED_CHANGES.CANCEL')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import unsavedChangesGuard from 'shared/mixins/unsavedChangesGuard';
import { required } from 'vuelidate/lib/validators';
import Modal from '../Modal.vue';

export default {
  mixins: [alertMixin, unsavedChangesGuard],
  components: { Modal },
  props: {
    value: {
      type: [Number, String, null],
      default: null,
    },
    allowCreate: {
      type: Boolean,
      default: true,
    },
    i18nPrefix: {
      type: String,
      default: 'CONTACT_FORM.FORM.COMPANY',
    },
  },
  data() {
    return {
      selectedCompany: null,
      showCreateModal: false,
      showDeleteCompanyModal: false,
      showUnsavedChangesConfirm: false,
      isEditMode: false,
      newCompanyName: '',
      newCompanyDocument: '',
      isSaving: false,
    };
  },
  validations() {
    return {
      newCompanyName: this.showCreateModal ? { required } : {},
      newCompanyDocument: this.showCreateModal ? { required } : {},
    };
  },
  computed: {
    ...mapGetters({
      companies: 'companies/getCompanies',
    }),

    newCompanyDocumentDisplay: {
      get() {
        return this.formatCompanyDocument(this.newCompanyDocument);
      },
      set(value) {
        this.newCompanyDocument = this.normalizeCompanyDocument(value);
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(companyId) {
        const normalizedId =
          companyId === null || companyId === undefined || companyId === ''
            ? null
            : Number(companyId);
        this.selectedCompany =
          this.companies.find(company => company.id === normalizedId) || null;
      },
    },
    companies: {
      immediate: true,
      handler() {
        const normalizedId =
          this.value === null || this.value === undefined || this.value === ''
            ? null
            : Number(this.value);
        this.selectedCompany =
          this.companies.find(company => company.id === normalizedId) || null;
      },
    },
    selectedCompany(company) {
      this.$emit('input', (company && company.id) || null);
    },
    showCreateModal: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          // Modal de criação/edição aberto, captura estado inicial
          setTimeout(() => {
            this.captureInitialState(this.getFormState());
          }, 100);
        }
      }
    }
  },
  mounted() {
    this.$store.dispatch('companies/get');
  },
  methods: {
    normalizeCompanyDocument(value) {
      const raw = (value || '').toString();
      const normalized = raw.replace(/[^0-9a-z]/gi, '').toUpperCase();
      return normalized.slice(0, 15);
    },

    formatCompanyDocument(rawValue) {
      const raw = this.normalizeCompanyDocument(rawValue);
      const hasLetters = /[A-Z]/.test(raw);
      return hasLetters
        ? this.formatAlphaNumericCompanyDocument(raw)
        : this.formatNumericCompanyDocument(raw);
    },

    formatAlphaNumericCompanyDocument(raw) {
      const value = (raw || '').slice(0, 15);
      const parts = [
        value.slice(0, 3),
        value.slice(3, 6),
        value.slice(6, 9),
        value.slice(9, 13),
        value.slice(13, 15),
      ].filter(Boolean);

      if (parts.length <= 1) return parts[0] || '';
      if (parts.length === 2) return `${parts[0]}.${parts[1]}`;
      if (parts.length === 3) return `${parts[0]}.${parts[1]}.${parts[2]}`;
      if (parts.length === 4)
        return `${parts[0]}.${parts[1]}.${parts[2]}/${parts[3]}`;
      return `${parts[0]}.${parts[1]}.${parts[2]}/${parts[3]}-${parts[4]}`;
    },

    formatNumericCompanyDocument(raw) {
      const digits = (raw || '').replace(/\D/g, '').slice(0, 14);
      const p1 = digits.slice(0, 2);
      const p2 = digits.slice(2, 5);
      const p3 = digits.slice(5, 8);
      const p4 = digits.slice(8, 12);
      const p5 = digits.slice(12, 14);

      if (!p2) return p1;
      if (!p3) return `${p1}.${p2}`;
      if (!p4) return `${p1}.${p2}.${p3}`;
      if (!p5) return `${p1}.${p2}.${p3}/${p4}`;
      return `${p1}.${p2}.${p3}/${p4}-${p5}`;
    },

    openCreateModal() {
      this.isEditMode = false;
      this.newCompanyName = '';
      this.newCompanyDocument = '';
      this.$v.newCompanyName.$reset();
      this.$v.newCompanyDocument.$reset();
      this.showCreateModal = true;
    },
    openEditModal() {
      if (!this.selectedCompany) return;
      this.isEditMode = true;
      this.newCompanyName = this.selectedCompany.name || '';
      this.newCompanyDocument = this.normalizeCompanyDocument(
        this.selectedCompany.document || ''
      );
      this.$v.newCompanyName.$reset();
      this.$v.newCompanyDocument.$reset();
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
    },

    /**
     * Retorna o estado atual do formulário de empresa.
     */
    getFormState() {
      return {
        newCompanyName: this.newCompanyName,
        newCompanyDocument: this.newCompanyDocument,
        isEditMode: this.isEditMode,
      };
    },

    /**
     * Intercepta o fechamento do modal para verificar alterações não salvas.
     */
    handleCloseWithGuard() {
      this.handleUnsavedClose(
        this.getFormState(),
        () => this.closeCreateModal(),
        () => { this.showUnsavedChangesConfirm = true; }
      );
    },

    openDeleteModal() {
      if (!this.selectedCompany) return;
      this.showDeleteCompanyModal = true;
    },
    closeDeleteModal() {
      this.showDeleteCompanyModal = false;
    },
    async confirmDeleteCompany() {
      if (!this.selectedCompany) return;
      try {
        this.isSaving = true;
        await this.$store.dispatch('companies/delete', this.selectedCompany.id);
        this.selectedCompany = null;
        this.closeDeleteModal();
        await this.$store.dispatch('companies/get');
      } catch (error) {
        const status = error && error.response && error.response.status;
        const message =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message;
        if (message) {
          const normalizedMessage = (message || '').toString().toLowerCase();
          const isForeignKeyViolation =
            normalizedMessage.includes('foreignkeyviolation') ||
            normalizedMessage.includes('violates foreign key constraint') ||
            normalizedMessage.includes('still referenced');
          if (isForeignKeyViolation) {
            this.showAlert(
              'Não foi possível deletar esta empresa porque ela está vinculada a tickets. Remova/atualize os tickets vinculados e tente novamente.'
            );
            return;
          }
          this.showAlert(message);
          return;
        }
        if (status) {
          this.showAlert(
            `${this.$t('CONTACT_FORM.ERROR_MESSAGE')} (${status})`
          );
          return;
        }
        this.showAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
      } finally {
        this.isSaving = false;
      }
    },
    async submitCompany() {
      this.$v.newCompanyName.$touch();
      this.$v.newCompanyDocument.$touch();

      if (
        this.$v.newCompanyName.$invalid ||
        this.$v.newCompanyDocument.$invalid
      ) {
        return;
      }

      try {
        this.isSaving = true;
        const payload = {
          name: this.newCompanyName,
          document: this.newCompanyDocument,
        };

        const response =
          this.isEditMode && this.selectedCompany
            ? await this.$store.dispatch('companies/update', {
                id: this.selectedCompany.id,
                ...payload,
              })
            : await this.$store.dispatch('companies/create', payload);

        const savedCompany = response?.data;
        if (savedCompany) {
          this.selectedCompany = savedCompany;
        }
        this.closeCreateModal();
      } catch (error) {
        const status = error && error.response && error.response.status;
        const message =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message;
        const attributes =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.attributes;

        if (status === 422) {
          const hasDocumentError =
            Array.isArray(attributes) && attributes.includes('document');
          if (hasDocumentError) {
            this.showAlert(this.$t('CONTACT_FORM.FORM.COMPANY.DUPLICATE_CNPJ'));
            return;
          }

          if (message) {
            const normalizedMessage = (message || '').toString().toLowerCase();
            const isDuplicateCnpj =
              normalizedMessage.includes('has already been taken') &&
              normalizedMessage.includes('document');
            if (isDuplicateCnpj) {
              this.showAlert(
                this.$t('CONTACT_FORM.FORM.COMPANY.DUPLICATE_CNPJ')
              );
              return;
            }

            this.showAlert(message);
            return;
          }
        }
        this.showAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .modal-container.modal-fit {
  padding: var(--space-normal);
  height: fit-content !important;
}

::v-deep .modal-container.modal-fit .content-box {
  width: 36rem;
  max-width: 90vw;
}

::v-deep .modal-container.modal-fit .content-box .medium-12.columns {
  padding: 0;
}

::v-deep .modal-container.modal-fit .modal-footer {
  padding-top: var(--space-small);
}

::v-deep .modal-container.modal-fit .modal-footer .medium-12.columns {
  padding: 0;
}

.company-picker-actions {
  display: flex;
  gap: var(--space-small);
  margin-top: var(--space-small);
  margin-bottom: var(--space-normal);
  flex-wrap: wrap;
}

::v-deep .company-picker-action {
  min-width: 7.5rem;
  justify-content: center;
}

::v-deep .company-picker-delete-modal.modal-mask {
  justify-content: center;
}

::v-deep .company-picker-delete-modal .modal-container {
  width: 42rem;
  max-width: 42rem;
  min-width: 42rem;
  height: auto;
  border-radius: var(--border-radius-medium);
}

.company-picker-delete-content {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.company-picker-delete-content .modal-footer.delete-item {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--space-small);
  padding-top: var(--space-small);
}

.company-picker-delete-content .action-button {
  flex: 0 0 auto;
}

</style>
